import { useState } from 'react';
import type { ApplicationDetail } from '../api/client';
import { useAdvance, useCastVote, useEditVote, useHire, useReject } from '../api/applications';

export default function ReviewPanel({ application }: { application: ApplicationDetail }) {
  const cast = useCastVote(application.id);
  const edit = useEditVote(application.id);
  const advance = useAdvance(application.id);
  const reject = useReject(application.id);
  const hire = useHire(application.id);

  const [comment, setComment] = useState(application.myReview?.comment ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const t = application.reviewTally;
  const pct = Math.round(t.approvalRatio * 100);
  const thresholdPct = Math.round(t.threshold * 100);
  const busy = cast.isPending || edit.isPending || advance.isPending || reject.isPending || hire.isPending;
  const onError = (e: unknown) => setError(e instanceof Error ? e.message : 'Action failed');

  const submitVote = (decision: 'APPROVED' | 'DENIED') => {
    setError(null);
    const mutation = application.canEditVote ? edit : cast;
    mutation.mutate({ decision, comment: comment.trim() || undefined }, { onError });
  };

  return (
    <div className="review-panel">
      {error && <div className="banner-error">{error}</div>}

      <div className="tally">
        <div className="tally-bar">
          <div className={t.meetsThreshold ? 'tally-fill ok' : 'tally-fill'} style={{ width: `${Math.min(pct, 100)}%` }} />
          <div className="tally-threshold" style={{ left: `${Math.min(thresholdPct, 100)}%` }} />
        </div>
        <div className="tally-stats muted small">
          {t.approvals} approve · {t.denials} deny · {t.votesCast}/{t.assignedReviewers} reviewers voted — {pct}% (threshold {thresholdPct}%)
          {t.meetsThreshold ? ' ✓ met' : ''}
        </div>
      </div>

      {application.myReview && !application.canEditVote && (
        <p className="muted small">
          You voted: <strong>{application.myReview.decision === 'APPROVED' ? 'Approve' : 'Deny'}</strong>
        </p>
      )}

      {(application.canVote || application.canEditVote) && (
        <div className="vote-box">
          {application.canEditVote && <p className="muted small">Edit your vote:</p>}
          <textarea rows={2} placeholder="Optional comment…" value={comment} onChange={(e) => setComment(e.target.value)} />
          <div className="form-actions">
            <button className="btn-primary" disabled={busy} onClick={() => submitVote('APPROVED')}>Approve</button>
            <button className="btn-ghost" disabled={busy} onClick={() => submitVote('DENIED')}>Deny</button>
          </div>
        </div>
      )}

      {application.reviews && application.reviews.length > 0 && (
        <table className="table">
          <thead><tr><th>Reviewer</th><th>Vote</th><th>Comment</th></tr></thead>
          <tbody>
            {application.reviews.map((r) => (
              <tr key={r.id}>
                <td>{r.reviewer.displayName}</td>
                <td><span className={r.decision === 'APPROVED' ? 'badge badge-open' : 'badge badge-rejected'}>{r.decision === 'APPROVED' ? 'Approve' : 'Deny'}</span></td>
                <td className="muted small">{r.comment ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {application.rejectionReason && (
        <p className="muted small">Rejection reason: {application.rejectionReason}</p>
      )}

      {(application.canAdvance || application.canHire || application.canReject) && (
        <div style={{ marginTop: '1rem' }}>
          {application.canReject && (
            <textarea
              className="reason-box"
              rows={2}
              placeholder="Optional rejection reason…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
          <div className="form-actions" style={{ marginTop: '0.5rem' }}>
            {application.canAdvance && (
              <button className="btn-primary" disabled={busy} onClick={() => { setError(null); advance.mutate(undefined, { onError }); }}>
                Advance to interview
              </button>
            )}
            {application.canHire && (
              <button className="btn-primary" disabled={busy} onClick={() => { setError(null); hire.mutate(undefined, { onError }); }}>
                Hire
              </button>
            )}
            {application.canReject && (
              <button className="btn-ghost" disabled={busy} onClick={() => { setError(null); reject.mutate(reason.trim() || undefined, { onError }); }}>
                Reject
              </button>
            )}
          </div>
        </div>
      )}

      {application.status === 'UNDER_REVIEW' && !t.meetsThreshold && application.canReject && (
        <p className="muted small">Approval threshold not met yet — advance unlocks at {thresholdPct}%.</p>
      )}
    </div>
  );
}
